import { createColorThemeConfig } from './createColorTheme'
import { createDecoratedAssets } from './createDecoratedAssets'
import { imageUrl } from './createThemeAssets'

const rgb = color => [1, 3, 5].map(i => parseInt(color.slice(i, i + 2), 16)).join(', ')

export function createDecoratedTheme({ name, label, art, colors }) {
    const c = colors
    const assets = createDecoratedAssets(art)
    const theme = createColorThemeConfig({
        name,
        label,
        assets,
        palette: {
            deep: c['page-bg'],
            deepRgb: rgb(c['page-bg']),
            mid: c.surface,
            midRgb: rgb(c.surface),
            main: c.raised,
            mainRgb: rgb(c.raised),
            control: c.input,
            border: c.border,
            focus: c.raised,
            surfaceRgb: rgb(c.surface),
            accent: c.primary,
            accentLight: c.gold,
            glow: c.gold,
            glowRgb: rgb(c.gold),
            iconBg: c.surface,
            softText: c.muted,
            softTextRgb: rgb(c.muted),
            brightText: c.text,
            brightRgb: rgb(c.text),
            noticeIconFilter: 'none'
        }
    })
    theme.design = 'decorated'
    Object.entries(c).filter(([, value]) => /^#[\da-f]{6}$/i.test(value)).forEach(([key, value]) => {
        theme.cssVars[`--skin-${key}`] = value
        theme.cssVars[`--skin-${key}-rgb`] = rgb(value)
    })
    Object.assign(theme.cssVars, {
        '--skin-backdrop': imageUrl(art.pageTexture),
        '--skin-sidebar': imageUrl(art.sidebar),
        '--skin-hero-scene': imageUrl(art.hero),
        '--skin-event-backdrop': imageUrl(art.pageTexture),
        '--skin-page-texture': imageUrl(art.pageTexture),
        '--skin-activity-scenes': imageUrl(art.activityScenes),
        '--skin-scratch-frame': imageUrl(art.scratchFrame),
        '--skin-frame': imageUrl(art.frame),
        '--skin-icons': imageUrl(art.icons),
        '--skin-home-activity-icons': imageUrl(art.homeActivityIcons || art.icons),
        '--skin-vip-icons': imageUrl(art.vipIcons),
        '--skin-detail-icons': imageUrl(art.detailIcons),
        '--skin-vip-emblem': imageUrl(art.vipEmblem),
        '--skin-vip-calendar': imageUrl(art.vipCalendar),
        '--skin-wallet-gift': imageUrl(art.walletGift),
        '--skin-wallet-treasure': imageUrl(art.walletTreasure),
        '--skin-wallet-lock': imageUrl(art.walletLock),
        '--skin-ranking-trophy': imageUrl(art.rankingTrophy),
        '--skin-payment-scenes': imageUrl(art.paymentScenes),
        '--skin-affiliate-megaphone': imageUrl(art.affiliateMegaphone),
        '--skin-auth-art': imageUrl(art.auth),
        '--skin-wheel-rim': imageUrl(art.wheelRim),
        '--skin-wheel-hub': imageUrl(art.wheelHub),
        '--skin-wheel-pointer': imageUrl(art.wheelPointer),
        '--skin-button-bg': c.button,
        '--skin-selected-bg': c.selection,
        '--theme-root-bg': c['page-bg'],
        '--theme-page-bg': imageUrl(art.pageTexture),
        '--theme-page-bg-size': '720px 720px',
        '--theme-page-bg-position': 'center top',
        '--theme-header-bg': c.surface,
        '--theme-active-text': c['on-primary'],
        '--theme-active-bg': c.button,
        '--theme-header-accent-bg': c.button,
        '--theme-gold-bg': c.button,
        '--theme-gold-text': c['on-primary'],
        '--theme-surface-bg': c.surface,
        '--theme-popup-bg': c.surface,
        '--theme-popup-panel-bg': c.surface,
        '--theme-popup-panel-fill': c.input,
        '--theme-popup-box-bg': c.surface,
        '--theme-popup-tabs-bg': c.input,
        '--theme-popup-mask-bg': 'rgba(0,0,0,.76)',
        '--theme-popup-overlay-bg': 'rgba(0,0,0,.78)',
        '--theme-popup-box-shadow': '0 20px 80px #0009, inset 0 1px 0 rgba(244,212,126,.12)',
        '--theme-popup-panel-shadow': '0 8px 26px #0004',
        '--theme-sidebar-bg': c.input,
        '--theme-sidebar-bg-size': 'cover',
        '--theme-sidebar-item-active-bg': c.selection,
        '--theme-sidebar-item-active-line': c.gold,
        '--theme-sidebar-service-bg': c.button,
        '--theme-tabbar-shell-bg': c.input,
        '--theme-tabbar-float-bg': c.input,
        '--theme-tabbar-active-bg': c.selection,
        '--theme-tabbar-active-text': c.gold,
        '--theme-tabbar-text': c.muted,
        '--theme-tabbar-active-bg-size': '100% 100%',
        '--theme-tabbar-active-bg-position': 'center',
        '--theme-signin-accent-text': c.gold,
        '--theme-signin-secondary-amount-text': c.gold,
        '--theme-signin-disabled-btn-bg': c.disabled,
        '--theme-signin-disabled-btn-text': c.muted,
        '--theme-popup-amount-text': c.gold,
        '--theme-game-title-bg': c.surface,
        '--theme-game-title-text': c.text,
        '--theme-person-vip-bg': c.surface,
        '--theme-person-vip-pc-bg': c.surface,
        '--theme-event-top-shell-bg': c.surface,
        '--theme-event-top-bg': c.input,
        '--theme-event-card-bg': c.surface,
        '--theme-home-classify-fixed-bg': c.input,
        '--theme-home-classify-item-bg': c.surface,
        '--theme-home-classify-nav-bg': c.surface,
        '--theme-home-banner-panel-bg': 'transparent',
        '--theme-input-focus-border': c.gold,
        '--theme-header-control-bg': c.input,
        '--theme-game-placeholder-surface': c.input,
        '--theme-game-placeholder-fallback': c.input,
        '--theme-footer-border-top': 'none',
        '--theme-footer-border-body': 'none',
        '--theme-footer-menu-title-line': 'none',
        '--theme-sidebar-menu-item-border': 'none',
        '--theme-signin-vip-icon': 'none',
        '--theme-signin-vip-icon-active': 'none',
        '--theme-signin-lock-icon': 'none'
    })
    for (const prefix of ['--theme-home-frame-corner-', '--theme-common-frame-corner-', '--theme-home-event-card-corner-']) {
        for (const corner of ['top-left', 'top-right', 'bottom-left', 'bottom-right']) theme.cssVars[prefix + corner] = 'none'
    }
    return theme
}
